import AdsApiService from '@/services/AdsApiService';
import firebaseAdsService from '@/services/firebaseAdsService';

/**
 * Where ad content comes from during the Firestore -> MySQL cutover.
 *
 * The API is the system of record. Firestore stays wired up as a fallback for
 * one release so a deploy ordering problem, or a branch context we cannot
 * resolve, degrades to the old behaviour instead of an empty carousel. Delete
 * the fallback (and firebaseAdsService with it) once the API path has been
 * running in production for a release.
 */

/**
 * Clinic and branch codes from the current URL.
 *
 * The ads cache is a module-level singleton with no router access, and the app
 * only ever serves ads from `/:clinicUrl/:branchUrl/...` routes, so the path is
 * the reliable source here.
 *
 * @returns {{clinicURL: string, branchURL: string}|null}
 */
export function branchContextFromLocation() {
  const segments = window.location.pathname.split('/').filter(Boolean);
  if (segments.length < 2) return null;

  const [clinicURL, branchURL] = segments;
  return { clinicURL, branchURL };
}

/**
 * The ads cache asks for the version and the ads back to back on a cache miss.
 * Both answers come from the same endpoint, so the response is memoised just
 * long enough to serve that pair from one round trip. Deliberately seconds, not
 * minutes: the real caching lives in useAdsCache, and this must never be the
 * reason a freshly published ad fails to appear.
 */
const RESPONSE_MEMO_MS = 5000;
let memo = { at: 0, key: null, promise: null };

const fetchAds = (context) => {
  const key = `${context.clinicURL}/${context.branchURL}`;
  const fresh = Date.now() - memo.at < RESPONSE_MEMO_MS;

  if (!fresh || memo.key !== key || !memo.promise) {
    memo = {
      at: Date.now(),
      key,
      promise: AdsApiService.getAds(context.clinicURL, context.branchURL).catch(
        (error) => {
          // A failed request must not be replayed from the memo.
          memo = { at: 0, key: null, promise: null };
          throw error;
        }
      ),
    };
  }

  return memo.promise;
};

/**
 * Reshape an API ad row into the document shape the component transformer
 * expects.
 *
 * Both sources carry the same type-specific `content` object, so the existing
 * Firestore transformer is reused rather than duplicated — that transformer is
 * the definition of what the carousel and popup components consume.
 */
const toDocumentShape = (row) => ({
  // `val` in the component shape, and the reference the tracker reports events
  // against. Migrated ads keep their Firestore id so their statistics stay
  // attached; console-authored ads use their row id.
  id: row.adRef || String(row.id),
  type: row.type || 'image',
  title: row.title || '',
  priority: row.priority || 0,
  isActive: true, // the API only returns ads that are live right now
  updatedAt: row.modifyDt || row.creationDt,
  content: {
    ...(row.content || {}),
    linkUrl: row.content?.linkUrl || row.linkUrl || '',
  },
});

/**
 * Ads in the shape the display components consume.
 *
 * @returns {Promise<Array>} formatted ads, empty array if every source fails
 */
export async function getFormattedAds() {
  const context = branchContextFromLocation();

  if (context) {
    try {
      const response = await fetchAds(context);
      const rows = response?.data?.ads;

      if (Array.isArray(rows) && rows.length > 0) {
        return firebaseAdsService.transformAdsForComponent(
          rows.map(toDocumentShape)
        );
      }

      // An empty list is ambiguous during the cutover: it could mean "no ads
      // configured yet" or "nothing imported yet". Falling through to Firestore
      // keeps ads on screen either way.
    } catch {
      // Fall through to Firestore.
    }
  }

  return firebaseAdsService.getFormattedAds();
}

/**
 * Cache version token.
 *
 * The API derives it from the newest ad write, so editing an ad in the console
 * invalidates client caches by itself. Falls back to the legacy
 * `config_value.ads_version` counter, which the old superadmin button bumps.
 *
 * @returns {Promise<string>}
 */
export async function getAdsVersion() {
  const context = branchContextFromLocation();

  if (context) {
    try {
      const response = await fetchAds(context);
      if (response?.data?.version) return String(response.data.version);
    } catch {
      // Fall through to the legacy counter.
    }
  }

  const axios = (await import('axios')).default;
  const response = await axios.get('/api/master/configValue/all');
  const adsVersionConfig = response.data.find(
    (config) => config.configName === 'ads_version'
  );

  return adsVersionConfig ? adsVersionConfig.val.toString() : '1';
}

export default { getFormattedAds, getAdsVersion, branchContextFromLocation };
